import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer";
import {
  ArrowLeft,
  Settings,
  Gauge,
  ShieldAlert,
  SlidersHorizontal,
  ArrowRight,
  Zap,
  ChevronRight,
} from "lucide-react";

const ManagedSASEDetail = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeComp, setActiveComp] = useState(0);

  // --- EXISTING DATA ---
  const challenges = [
    {
      title: "SASE is deployed, not operationalized",
      points: [
        "SASE features are enabled but not actively managed",
        "Policies become outdated and misaligned",
        "Security posture weakens over time",
      ],
      icon: <Settings className="text-[#34d399]" size={32} strokeWidth={1.5} />,
    },
    {
      title: "Performance becomes the blame game",
      points: [
        "User experience issues increase",
        "SASE is blamed without evidence",
        "Root causes stay hidden",
      ],
      icon: <Gauge className="text-[#34d399]" size={32} strokeWidth={1.5} />,
    },
    {
      title: "Zero Trust stops at login",
      points: [
        "Identity is checked only once",
        "Risk continues to change",
        "Implicit trust is created",
      ],
      icon: (
        <ShieldAlert className="text-[#34d399]" size={32} strokeWidth={1.5} />
      ),
    },
    {
      title: "Too many controls, no ownership",
      points: [
        "Policies spread across many platforms",
        "No single team owns security outcomes",
        "Gaps and conflicts grow",
      ],
      icon: (
        <SlidersHorizontal
          className="text-[#24ff81]"
          size={32}
          strokeWidth={1.5}
        />
      ),
    },
  ];

  const saseComponents = [
    {
      title: "Software-Defined WAN (SD-WAN)",
      desc: "SD-WAN enables optimal WAN management. SASE leverages SD-WAN capabilities to provide optimized network routing, global connectivity, WAN and Internet security, cloud acceleration, and remote access.",
      image:
        "https://res.cloudinary.com/dpn3bkjba/image/upload/v1771578990/Software-Defined-WAN-_mt6kcv.svg",
    },
    {
      title: "Secure Web Gateway (SWG)",
      desc: "SWG protects users against malware, phishing, and other web-borne threats. SASE offers SWG protection to all users, at all locations, without the added latency of routing traffic to inspection in specific locations.",
      image:
        "https://res.cloudinary.com/dpn3bkjba/image/upload/v1771579040/Software-Defined-WAN-2_zykvx9.svg",
    },
    {
      title: "Firewall as a Service (FWaaS)",
      desc: "A firewall is the foundation of any network security stack. SASE includes FWaaS to provide the scalability and elasticity needed for the digital business and to extend a full network security stack wherever needed.",
      image:
        "https://res.cloudinary.com/dpn3bkjba/image/upload/v1771579039/Zero-Trust-Network-Access-1_nejott.svg",
    },
    {
      title: "Zero-Trust Network Access (ZTNA)",
      desc: "ZTNA replaces legacy VPNs. It provides granular, identity-based access control to private applications, ensuring that only authorized users can access specific resources based on context and policy.",
      image:
        "https://res.cloudinary.com/dpn3bkjba/image/upload/v1771579039/Zero-Trust-Network-Access-1_nejott.svg",
    },
    {
      title: "Cloud Access Security Broker (CASB)",
      desc: "CASB helps enterprises monitor the usage of SaaS applications, both sanctioned and unsanctioned (“Shadow IT”), and apply access policies based on user identity, application risk, and nature of the action and data being used.",
      image:
        "https://res.cloudinary.com/dpn3bkjba/image/upload/v1771579039/3-1_edt5rm.svg",
    },
    {
      title: "Global Cloud Network",
      desc: "The Cloud Network optimizes global routing, latency sensitive traffic such as voice and video, and cloud and on-premises application access for all sources and destinations.",
      image:
        "https://res.cloudinary.com/dpn3bkjba/image/upload/v1771579039/Cloud-Network-_jxfzq9.svg",
    },
    {
      title: "Unified Management",
      desc: "SASE solves the complexity of managing multiple disparate products. A true SASE allows users to monitor and manage all network and security solutions from a single pane of glass.",
      image:
        "https://res.cloudinary.com/dpn3bkjba/image/upload/v1771579038/Unified-Management_jtr9fi.svg",
    },
  ];

  // --- NEW DATA INTEGRATION ---
  // const realityCards = [
  //   {
  //     heading: "The network perimeter no longer exists",
  //     desc: "Users access business apps from homes, airports, coffee shops, and unmanaged networks. Traditional perimeter-based security collapses under remote and hybrid work, leaving implicit trust where none should exist.",
  //   },
  //   {
  //     heading: "Security policies are inconsistent and brittle",
  //     desc: "Different tools enforce different rules for web, cloud, and private apps. What’s blocked in one place is allowed in another. Policy drift becomes inevitable, and attackers exploit the weakest link.",
  //   },
  //   {
  //     heading: "VPNs degrade both security and user experience",
  //     desc: "VPNs grant excessive network-level access and create performance bottlenecks. They increase lateral movement risk while frustrating users with slow, unstable connections.",
  //   },
  //   {
  //     heading: "Cloud and SaaS traffic lacks deep inspection",
  //     desc: "Most organizations still struggle to inspect encrypted traffic, SaaS APIs, and shadow IT usage. Sensitive data moves freely without consistent DLP, threat prevention, or identity-based controls.",
  //   },
  //   {
  //     heading: "SASE platforms are powerful — but complex to run",
  //     desc: "Deploying SASE is not the same as operating it. Misconfigurations, poor policy design, and lack of continuous tuning turn a strong architecture into an expensive, underutilized toolset.",
  //   },
  // ];

  const operationalSteps = [
    {
      title: "Identity-Centric Architecture Design",
      desc: "We redesign access around user identity, device posture, and application context, not IP addresses or network location. Every connection is explicitly verified.",
      owns: [
        "SASE architecture design",
        "Identity provider integration",
        "Zero trust access models",
        "Segmentation strategy",
      ],
      outcome:
        "Users get only the access they need nothing more, nothing less.",
    },
    {
      title: "Unified Policy Framework Across All Traffic",
      desc: "We implement a single, consistent policy model across web, SaaS, private apps, and cloud workloads eliminating rule conflicts and enforcement gaps.",
      owns: [
        "Policy design",
        "Secure web gateway rules",
        "CASB controls",
        "DLP alignment",
        "Application-specific enforcement",
      ],
      outcome:
        "Predictable security behavior across all users and applications.",
    },
    {
      title: "Secure Access Without VPNs",
      desc: "We replace legacy VPN access with application-level access that prevents lateral movement and minimizes blast radius.",
      owns: [
        "ZTNA configuration",
        "App onboarding",
        "Connector health",
        "Access lifecycle management",
      ],
      outcome:
        "Faster access for users. Stronger containment against attackers.",
    },
    {
      title: "Continuous Threat Prevention & Inline Inspection",
      desc: "All traffic is inspected inline for malware, command-and-control activity, data exfiltration, and policy violations including encrypted and SaaS traffic.",
      owns: [
        "Threat prevention tuning",
        "SSL inspection strategy",
        "IPS configuration",
        "Risk-based enforcement",
      ],
      outcome:
        "Threats are blocked in transit, not discovered after damage is done.",
    },
    {
      title: "Ongoing Optimization & Operational Management",
      desc: "Cloud apps change. Users move. Attack techniques evolve. We continuously tune policies, performance, and controls to keep security effective without breaking productivity.",
      owns: [
        "Change management",
        "Policy optimization",
        "Performance monitoring",
        "Platform health checks",
      ],
      outcome: "Security improves over time instead of degrading silently.",
    },
    {
      title: "Visibility, Reporting & Governance",
      desc: "You get clear visibility into access patterns, risk posture, policy effectiveness, and compliance alignment translated for both technical and executive audiences.",
      owns: [
        "Operational dashboards",
        "Executive reporting",
        "Compliance evidence",
        "Audit support",
      ],
      outcome: "Defensible security posture with measurable business value.",
    },
  ];

  const keyBenefits = [
    {
      title: "Zero Trust Access, Everywhere",
      desc: "Secure access for any user, on any device, to any application without VPN risk.",
    },
    {
      title: "Consistent Policy Enforcement",
      desc: "One security model across web, cloud, SaaS, and private apps.",
    },
    {
      title: "Improved User Experience",
      desc: "Faster, more reliable access without backhauling traffic.",
    },
    {
      title: "Reduced Operational Burden",
      desc: "We run the platform so your team doesn’t have to.",
    },
    {
      title: "Maximum ROI from SASE Investments",
      desc: "We extract real value from the platform you’ve already paid for.",
    },
  ];

  const faqItems = [
    {
      q: "What is SASE used for?",
      a: "Secure access service edge (SASE) is used to deliver converged enterprise network and security services from a globally distributed cloud service.",
    },
    {
      q: "What is the difference between point solutions and SASE?",
      a: "Point solutions address individual problems, while SASE converges networking and security into a unified, cloud-delivered platform.",
    },
    {
      q: "Why is SASE important?",
      a: "SASE enables secure, scalable access for users and applications everywhere, supporting modern hybrid work.",
    },
  ];

  const FAQItem = ({ item }) => {
    const [open, setOpen] = useState(false);
    return (
      <div className="border-t border-[#24ff81]/40 pt-6">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex justify-between items-start text-left gap-6"
        >
          <h3 className="text-white text-lg md:text-xl font-medium">
            {item.q}
          </h3>
          <span className="text-[#24ff81] text-2xl">{open ? "−" : "+"}</span>
        </button>
        <div
          className={`overflow-hidden transition-all duration-300 ${open ? "max-h-[500px] mt-4" : "max-h-0"}`}
        >
          <p className="text-white/70 text-sm md:text-base leading-relaxed">
            {item.a}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>
          Managed SASE Services | Secure Access Service Edge | Synthera Security
        </title>

        <meta
          name="description"
          content="Managed SASE services combining Zero Trust Network Access, Secure Web Gateway, CASB, FWaaS, SD-WAN, and continuous security operations to protect modern enterprises."
        />

        <meta
          name="keywords"
          content="Managed SASE, Secure Access Service Edge, Zero Trust Network Access, ZTNA, CASB, SWG, FWaaS, SD-WAN, Secure Access, Cloud Security, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Managed SASE Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="Transform secure access with managed SASE services, Zero Trust security, SD-WAN optimization, CASB controls, and continuous operational management."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/services/managed-sase"
        />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/services/managed-sase"
        />

        <meta
          property="og:image"
          content="https://images.unsplash.com/photo-1558494949-ef010cbdcc51?q=80&w=2000"
        />
      </Helmet>

      <div className="bg-white min-h-screen font-sans text-slate-900">
        {/* 1. TOP HERO SECTION */}
        <section className="bg-[#032d28] text-white relative overflow-hidden flex flex-col items-center py-24 px-6">
          <Link
            to="/services/managed-security-services"
            className="absolute top-20 left-8 text-white/40 hover:text-[#06573d] transition-colors"
          >
            <ArrowLeft size={24} />
          </Link>
          <div className="container mx-auto px-6 py-12">
            <div className="text-center max-w-5xl mx-auto mb-16">
              <span className="text-white/40 text-xs tracking-[0.2em] uppercase font-bold mb-4 block">
                Traditional SASE deployments can't keep up
              </span>
              <h1 className="text-white text-4xl md:text-6xl font-bold leading-tight">
                SASE promises <span className="text-[#24ff81]">simplicity</span>{" "}
                — reality delivers complexity
              </h1>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {challenges.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                  className="group bg-white p-6 rounded-3xl shadow-lg flex flex-col min-h-[260px] cursor-pointer transition-all"
                >
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-[#34d399] text-base font-bold uppercase mb-4">
                    {item.title}
                  </h3>
                  <div className="flex flex-col w-full">
                    {item.points.map((line, i) => (
                      <div key={i}>
                        <p className="text-[#0c3f38]/70 text-sm font-medium py-1">
                          {line}
                        </p>
                        {i !== item.points.length - 1 && (
                          <div className="h-px w-full bg-[#24ff81]/40 my-[2px]" />
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* --- ADDED: REALITY CARDS SECTION ---
        <div className="container mx-auto max-w-[1400px] relative z-10 mt-32">
          <div className="text-center mb-12">
            <p className="text-[#24ff81] text-xs font-bold uppercase tracking-[0.3em] opacity-80">
              The Reality Organizations Face Today
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-0 border border-white/10 rounded-[45px] overflow-hidden shadow-2xl">
            {realityCards.map((card, index) => (
              <motion.div
                key={index}
                whileHover={{ backgroundColor: "#f8fafc", y: -5 }}
                className="bg-white p-8 flex flex-col text-left min-h-[380px] border-r border-gray-100 last:border-r-0 cursor-pointer transition-colors"
              >
                <h3 className="text-[#032d28] text-xl font-extrabold mb-6 leading-tight uppercase">
                  {card.heading}
                </h3>
                <p className="text-slate-600 text-sm font-medium leading-relaxed">
                  {card.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div> */}
        </section>
        {/* 
      --- ADDED: SERVICE OVERVIEW SECTION ---
      <section className="py-32 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-[#032d28] text-4xl md:text-5xl font-black uppercase mb-8 leading-tight tracking-tight">
                How Managed SASE Fixes Risk
              </h2>
              <div className="space-y-6 text-slate-600 text-lg leading-relaxed font-medium">
                <p>
                  SASE fails when treated as a one-time rollout. Managed SASE
                  succeeds when access, identity, data, and threat controls are
                  continuously operated as a living system.
                </p>
                <p className="bg-slate-50 p-6 rounded-2xl border-l-4 border-[#24ff81]">
                  Our service focuses on secure access outcomes, not just
                  box-ticking deployments.
                </p>
              </div>
            </motion.div>
            <div className="relative group">
              <div className="absolute inset-0 bg-[#24ff81]/10 rounded-[48px] rotate-3 group-hover:rotate-0 transition-transform duration-500" />
              <img
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc51?q=80&w=2000"
                className="relative rounded-[48px] shadow-2xl w-full h-[500px] object-cover"
                alt="SASE Security"
              />
            </div>
          </div>
        </div>
      </section> */}

        {/* --- ADDED: BEAUTIFIED OPERATIONAL PROCESS DATA --- */}

        {/* ===== PREMIUM OPERATIONAL CARDS ===== */}
        <section className="relative py-28 bg-[#071c18] overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(54,168,138,.15),transparent)]" />

          <div className="container mx-auto px-6 max-w-7xl relative z-10">
            {/* Heading */}
            <div className="text-center mb-20">
              <p className="text-emerald-400 uppercase tracking-[0.3em] text-xs font-bold mb-6">
                Managed SASE Operations
              </p>
              <h2 className="text-white text-4xl md:text-6xl font-black leading-tight">
                Operational{" "}
                <span className="text-emerald-400">Execution Model</span>
              </h2>
            </div>
            {/* Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {operationalSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="forest-card rounded-3xl p-8 group cursor-pointer"
                >
                  {/* Icon */}
                  <div className="icon-container w-14 h-14 rounded-2xl flex items-center justify-center mb-8">
                    <ShieldAlert className="text-emerald-400" size={26} />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-emerald-300 transition">
                    {step.title}
                  </h3>

                  {/* Desc */}
                  <p className="text-emerald-100/60 text-sm leading-relaxed mb-8">
                    {step.desc}
                  </p>

                  {/* Divider */}
                  <div className="border-t border-emerald-800/30 pt-6">
                    <p className="text-xs font-semibold text-emerald-500/70 uppercase tracking-widest mb-5">
                      Service Ownership
                    </p>

                    <ul className="space-y-3">
                      {step.owns.map((item, i) => (
                        <li
                          key={i}
                          className="service-item flex items-center text-sm text-emerald-100/80"
                        >
                          <ChevronRight
                            className="mr-3 text-emerald-400"
                            size={16}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 2. KEY COMPONENTS SECTION (INTERACTIVE - PRESERVED) */}
        <section className="py-24 px-6 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="bg-[#032d28] rounded-[40px] p-10 md:p-20 flex flex-col lg:flex-row items-center gap-12 lg:gap-20 overflow-hidden">
              <div className="w-full lg:w-1/2">
                <h2 className="text-white text-3xl md:text-4xl font-bold mb-12">
                  The Key Components of SASE
                </h2>
                <div className="flex flex-col space-y-4">
                  {saseComponents.map((comp, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveComp(idx)}
                      className={`text-left text-2xl md:text-3xl font-bold transition-all flex items-center gap-4 ${activeComp === idx ? "text-[#24ff81]" : "text-white/50 hover:text-white"}`}
                    >
                      {activeComp === idx && <ArrowRight size={28} />}{" "}
                      {comp.title}
                    </button>
                  ))}
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeComp}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="bg-white rounded-[40px] p-8 md:p-12 shadow-2xl min-h-[500px] flex flex-col items-center justify-center text-center"
                  >
                    <img
                      src={saseComponents[activeComp].image}
                      alt={saseComponents[activeComp].title}
                      className="w-full max-w-[320px] h-auto mb-10"
                    />
                    <p className="text-slate-600 text-lg leading-relaxed border-t border-slate-100 pt-8">
                      {saseComponents[activeComp].desc}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>

        {/* ================= KEY BENEFITS PREMIUM BALANCED ================= */}
        <section className="py-28 bg-[#071c18] relative overflow-hidden">
          {/* subtle grid */}
          <div
            className="absolute inset-0 opacity-10 
    bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),
    linear-gradient(to_bottom,#80808012_1px,transparent_1px)] 
    bg-[size:40px_40px]"
          />

          {/* heading */}
          <div className="max-w-7xl mx-auto text-center mb-20 px-6 relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight">
              Key Benefits
            </h2>

            <p className="text-emerald-100/60 text-lg max-w-2xl mx-auto">
              Real outcomes from a fully managed SASE environment security,
              performance, and simplicity.
            </p>
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            {/* ROW 1 — first 3 cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {keyBenefits.slice(0, 3).map((b, i) => (
                <div
                  key={i}
                  className="group relative h-[230px] p-8
          bg-[#031f1c] border border-white/10
          transition-all duration-500
          hover:bg-gradient-to-br hover:from-[#0b3d35] hover:to-[#032d28]
          hover:border-emerald-400/40
          hover:shadow-[0_0_35px_rgba(36,255,129,0.12)]"
                >
                  {/* glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(36,255,129,0.18),transparent_60%)]" />
                  </div>

                  <div className="relative z-10">
                    <Zap className="text-emerald-400 mb-5" size={26} />

                    <h3 className="text-xl font-semibold text-white mb-3">
                      {b.title}
                    </h3>

                    <p className="text-white/70 text-sm leading-relaxed">
                      {b.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* ROW 2 — centered 2 cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 mt-0 md:max-w-3xl mx-auto">
              {keyBenefits.slice(3, 5).map((b, i) => (
                <div
                  key={i}
                  className="group relative h-[230px] p-8
          bg-[#031f1c] border border-white/10
          transition-all duration-500
          hover:bg-gradient-to-br hover:from-[#0b3d35] hover:to-[#032d28]
          hover:border-emerald-400/40
          hover:shadow-[0_0_35px_rgba(36,255,129,0.12)]"
                >
                  {/* glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(36,255,129,0.18),transparent_60%)]" />
                  </div>

                  <div className="relative z-10">
                    <Zap className="text-emerald-400 mb-5" size={26} />

                    <h3 className="text-xl font-semibold text-white mb-3">
                      {b.title}
                    </h3>

                    <p className="text-white/70 text-sm leading-relaxed">
                      {b.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. FAQ SECTION (PRESERVED) */}
        <section className="bg-[#071c18] py-28 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[#065c41] text-4xl md:text-6xl font-semibold text-center mb-16">
              FAQ
            </h2>
            <div className="space-y-6">
              {faqItems.map((item, index) => (
                <FAQItem key={index} item={item} />
              ))}
            </div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default ManagedSASEDetail;
